import { useEffect, useState } from 'react';

import { URLS } from '@/data';
import { DEPOSIT_STATUS_UNIVERSAL_LINK_URL, PaymentMethodTypes } from '@/feature/deposit/const';
import { productNames } from '@/hooks/use-active-product-name';
import { getAuthUserConfig, kycVerifiedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useAuthUserInfo } from '@/hooks/use-auth-user-info';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { save } from '@/utils/async-storage';

import { TrustlyEstablishData, TrustlyRefreshSplitTokenEstablishData, TrustlyStatus } from '../../../types';
import { TRUSTLY_APP_TO_APP_RETURN_URL, TRUSTLY_KYC_ONLY_UNIVERSAL_LINK_URL } from '../utils/const';
import { useTrustlyConfigurations } from './use-trustly-configurations';

type EstablishDataParams = {
    amount: string;
    isRefreshSplitToken: boolean;
    paymentTypeId?: string;
    transactionId?: string;
    isTrustlyIdOnly?: boolean;
};

// App-to-app (Mobile OAuth) sends the user into their banking app and returns them
// to Betr via the `betrpickem://` app scheme
const trustlyAppToAppMetadata = {
    integrationContext: 'InAppBrowser' as const,
    urlScheme: TRUSTLY_APP_TO_APP_RETURN_URL,
};

const generateUrls = (amount: string, paymentProvider: string, status: TrustlyStatus, isKycOnly: boolean = false) => {
    const trustlyUniversalDeeplinkUrl = isKycOnly
        ? TRUSTLY_KYC_ONLY_UNIVERSAL_LINK_URL
        : DEPOSIT_STATUS_UNIVERSAL_LINK_URL;

    if (isKycOnly) {
        return {
            returnUrl: `${trustlyUniversalDeeplinkUrl}?currentStatus=trustly_kyc_only_success`,
            cancelUrl: `${trustlyUniversalDeeplinkUrl}?currentStatus=trustly_kyc_only_failure`,
        };
    }

    return {
        returnUrl: `${trustlyUniversalDeeplinkUrl}?selectedAmount=${amount}&paymentProvider=${paymentProvider}&currentStatus=${status}`,
        cancelUrl: `${trustlyUniversalDeeplinkUrl}?selectedAmount=${amount}&paymentProvider=${paymentProvider}&currentStatus=${
            status === TrustlyStatus.REFRESH ? TrustlyStatus.REFRESH_CANCEL : TrustlyStatus.CANCEL
        }`,
    };
};

export const useEstablishData = ({
    amount,
    isRefreshSplitToken,
    paymentTypeId = '',
    transactionId = '',
    isTrustlyIdOnly = false,
}: EstablishDataParams): TrustlyRefreshSplitTokenEstablishData | TrustlyEstablishData => {
    const profile = useUserStore(state => state.profile);
    const { accessId, merchantId } = useTrustlyConfigurations();
    const commonEstablishData = {
        accessId,
        merchantId,
        amount: '0.00', // Do not change this from 0.00. This is not the passed value, it's the limit value for each transactionId.
        currency: 'USD',
        notificationUrl: URLS.TRUSTLY_WEBHOOK_URL,
        kycType: 1,
    };
    const userId = profile.sub;
    const activeProduct = useJurisdictionStore(state => state.product);
    // Predictions deposits are processed under the Fantasy merchant reference
    const productName =
        productNames[activeProduct === Product.Prediction ? Product.Pickem : activeProduct].toUpperCase();
    const merchantReference = `${productName}-${Date.now()}-${userId}`;
    const { data: isKycVerified } = useAuthUserConfig({ select: kycVerifiedSelector });
    const { data: userInfo } = useAuthUserInfo();
    const shouldPerformTrustlyKyc = isTrustlyIdOnly || !isKycVerified;
    // If kycType is 1, Trustly will perform KYC verification for the user
    // If kycType is 0, then Trustly will ignore verification. This is used for deposit flow without verification
    const kycType = shouldPerformTrustlyKyc ? 1 : 0;

    const [userCreatedTimestamp, setUserCreatedTimestamp] = useState<number>(0);

    useEffect(() => {
        if (!isRefreshSplitToken) {
            const initiateTrustlyProcess = async () => {
                try {
                    const userConfig = await getAuthUserConfig();
                    const { USER_CREATED_AT_TIMESTAMP_MS } = userConfig.data.config;
                    setUserCreatedTimestamp(USER_CREATED_AT_TIMESTAMP_MS ?? 0);

                    // Reset async storage hasProcessedTrustlyKyc for refresh flow
                    await save('hasProcessedTrustlyKyc', false);
                } catch (error) {
                    console.log('Failed to initiate Trustly process', error);
                    setUserCreatedTimestamp(0);
                }
            };

            initiateTrustlyProcess();
        }
    }, [isRefreshSplitToken]);

    if (isRefreshSplitToken) {
        const { returnUrl, cancelUrl } = generateUrls(amount, paymentTypeId, TrustlyStatus.REFRESH);

        return {
            ...commonEstablishData,
            kycType,
            merchantReference,
            paymentType: 'Verification',
            displayAmount: amount,
            returnUrl,
            cancelUrl,
            authToken: 'new',
            transactionId,
            metadata: trustlyAppToAppMetadata,
        } as TrustlyRefreshSplitTokenEstablishData;
    } else {
        const { returnUrl, cancelUrl } = generateUrls(
            amount,
            PaymentMethodTypes.Trustly,
            TrustlyStatus.TRANSACTION,
            isTrustlyIdOnly
        );

        const baseEstablishData = {
            ...commonEstablishData,
            kycType,
            merchantReference,
            requestSignature: 'REQUEST SIGNATURE',
            description: 'Betr Fantasy & Sportsbook',
            paymentType: 'Deferred',
            displayAmount: amount,
            customer: {
                externalId: profile.sub,
                enrollDate: userCreatedTimestamp,
                ...(kycType === 0 && {
                    taxId: `crypt2:${userInfo?.encryptedSSN9}`, // Use encrypted SSN for Trustly Deposit
                    name: `${userInfo?.first_name} ${userInfo?.last_name}`,
                    phone: userInfo?.phone_number,
                    email: profile.email,
                    dateOfBirth: `${userInfo?.dob?.dob_year}-${userInfo?.dob?.dob_month}-${userInfo?.dob?.dob_day}`,
                    address: {
                        address1: userInfo?.address?.street_name,
                        city: userInfo?.address?.city,
                        state: userInfo?.address?.state,
                        zip: userInfo?.address?.post_code,
                        country: userInfo?.address?.country,
                    },
                }),
            },
            returnUrl,
            cancelUrl,
        };

        return {
            ...baseEstablishData,
            metadata: {
                flowType: 'ID_CAPTURE',
                ...trustlyAppToAppMetadata,
            },
        } as TrustlyEstablishData;
    }
};
